import React, { KeyboardEvent, useMemo } from 'react';
import {
  VirtualizedTableColumnFit,
  VirtualizedTableColumnProps,
  VirtualizedTableRowAction,
  VirtualizedTableSelectionType,
  VirtualizedTableSort,
  VirtualizedTableSortDirection,
} from './VirtualizedTable.interface';
import classNames from 'classnames';
import { ArrowDown20Regular, ArrowUp20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { calculateGridTemplateColumns } from './VirtualizedTable.utils';

export interface VirtualizedTableHeaderPops {
  actions: VirtualizedTableRowAction[] | undefined;
  columns: VirtualizedTableColumnProps[];
  sort: VirtualizedTableSort | null;
  hasRowIcons: boolean;
  selectionType: VirtualizedTableSelectionType;
  columnFit: VirtualizedTableColumnFit;
  onColumnHeaderClick: (columnIndex: number) => void;
}

export const VirtualizedTableHeader: React.FC<VirtualizedTableHeaderPops> = ({
  actions,
  columns,
  sort,
  hasRowIcons,
  selectionType,
  columnFit,
  onColumnHeaderClick,
}) => {
  const { t } = useTranslation();

  const renderSortDirectionArrow = (colIndex: number) => {
    if (sort === null || sort.column !== colIndex) {
      return <></>;
    }

    return sort.direction === VirtualizedTableSortDirection.Asc ? (
      <ArrowUp20Regular />
    ) : (
      <ArrowDown20Regular />
    );
  };

  const gridTemplateColumns: string = useMemo(() => {
    return calculateGridTemplateColumns(
      selectionType !== VirtualizedTableSelectionType.None,
      hasRowIcons,
      columnFit === VirtualizedTableColumnFit.Fit,
      actions !== undefined,
      columns,
    );
  }, [selectionType, hasRowIcons, columnFit, actions, columns]);

  return (
    <div
      role="row"
      className="tableHeader row"
      style={{
        gridTemplateColumns: `${gridTemplateColumns}`,
      }}
    >
      {hasRowIcons && (
        <div
          className="iconCol cell"
          key="icon"
          aria-sort="none"
          aria-label={t('table_row_icon_column', { defaultValue: 'Spalte mit Icons' })}
          role="columnheader"
        ></div>
      )}
      {selectionType !== VirtualizedTableSelectionType.None && (
        <div
          role="columnheader"
          aria-sort="none"
          aria-label={t('table_row_selection_column', { defaultValue: 'Spalte mit Checkboxen zur Auswahl der jeweiligen Zeile' })}
          key="checkbox"
          className="checkboxCol cell"
        ></div>
      )}
      {columns.map((column, columnIndex) => {
        return (
          <div
            role="columnheader"
            aria-sort={
              !column.sorting || sort?.column !== columnIndex
                ? 'none'
                : sort?.direction === VirtualizedTableSortDirection.Asc
                ? 'ascending'
                : 'descending'
            }
            key={columnIndex}
            className={classNames('cell', { sorting: column.sorting })}
            tabIndex={0}
            onClick={() => column.sorting && onColumnHeaderClick(columnIndex)}
            onKeyUp={(event: KeyboardEvent<HTMLDivElement>) => {
              if (event.code === 'Space') {
                column.sorting && onColumnHeaderClick(columnIndex);
              }
            }}
            aria-label={t('table_sort_by_column', { defaultValue: 'Sortiert die Einträge nach dieser Spalte durch Drücken der Leertaste' })}
            style={
              columnFit !== VirtualizedTableColumnFit.Fit && column.width
                ? { width: column.width }
                : {}
            }
          >
            {column.header} {renderSortDirectionArrow(columnIndex)}
          </div>
        );
      })}
      {actions && (
        <div
          role="columnheader"
          aria-sort="none"
          aria-label={t('table_row_actions_column', { defaultValue: 'Spalte mit Aktionen für die jeweilige Zeile' })}
          key="actionsCol"
          className="actionsCol cell"
          style={{ width: `${actions.length * 32 + 22}px` }}
        ></div>
      )}
    </div>
  );
};
